import { miniApp } from '@tma.js/sdk-vue';
import { onBeforeUnmount, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useSessionStore } from '@/stores/session';

const LOCK_PATHS = ['/lock', '/lock/reset'];

/**
 * Mounted once in App.vue (spec 2026-10-30 §6.3). Two jobs:
 * 1. When the app comes back from the background after `resumeLockSeconds`, re-lock. Both
 *    `document.visibilitychange` and Telegram's `miniApp.isActive` signal (Bot API 8.0+) feed the
 *    same state, whichever fires first; a second "hidden" never overwrites the first timestamp.
 * 2. Whenever `pinLocked` flips to true (resume, or a 423 from any store call), go to /lock with
 *    the current route as `next`. The router guard handles navigation; this handles the rest.
 */
export function usePinLock(): void {
  const session = useSessionStore();
  const router = useRouter();
  // Date.now(), not performance.now(): the latter can freeze while a webview is backgrounded.
  // A clock that jumped backwards gives a negative pause, which locks rather than skips.
  let backgroundedAt: number | null = null;

  function onHidden(): void {
    if (backgroundedAt === null) backgroundedAt = Date.now();
  }

  function onShown(): void {
    if (backgroundedAt === null) return;
    const pausedMs = Date.now() - backgroundedAt;
    backgroundedAt = null;
    if (!session.profile?.pinEnabled) return;
    if (pausedMs < 0 || pausedMs >= session.resumeLockSeconds * 1000) session.relock();
  }

  function onVisibilityChange(): void {
    if (document.visibilityState === 'hidden') onHidden();
    else onShown();
  }

  function onActiveChange(active: boolean): void {
    if (active) onShown();
    else onHidden();
  }

  let unsubscribe: (() => void) | undefined;

  onMounted(() => {
    document.addEventListener('visibilitychange', onVisibilityChange);
    try {
      unsubscribe = miniApp.isActive.sub(onActiveChange);
    } catch {
      // An old client without the signal: visibilitychange alone covers it.
    }
  });

  onBeforeUnmount(() => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    unsubscribe?.();
  });

  watch(
    () => session.pinLocked,
    (locked) => {
      if (!locked) return;
      // Same condition as the router guard: blocked/pending users stay on their status screen.
      if (session.profile?.status !== 'active') return;
      const current = router.currentRoute.value;
      if (LOCK_PATHS.includes(current.path)) return;
      const next =
        current.path === '/loading'
          ? typeof current.query.next === 'string'
            ? current.query.next
            : '/'
          : current.fullPath;
      void router.replace({ path: '/lock', query: { next } });
    },
  );
}
